import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { ContractSortFields, SortDirection, GetContractsQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/contract';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useContractsTable = ({ queryOptions }: { queryOptions?: GetContractsQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetContractsQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as ContractSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withUser: true,
                  withTenant: true,
                  withProject: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: contractData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetContractsQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      contractData
        ? { totalCount: contractData.contracts.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [contractData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type ContractsBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function ContractsBaseTable(props: ContractsBaseTableProps) {
  const tableProps = useContractsTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
